import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/shared/lib/cn';

/** Mesmo mapa de cores da V1 (`components/dashboard/StatsCard.tsx`). */
const CORES = {
  pink: 'bg-brand',
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  orange: 'bg-orange-500',
  yellow: 'bg-amber-500',
} as const;

export type CorStatsCard = keyof typeof CORES;

interface Props {
  titulo: string;
  valor: number | string;
  icone: LucideIcon;
  cor: CorStatsCard;
  tendencia?: string;
  /** Quando passado, o chip do ícone vira atalho para a tela correspondente. */
  paraOIcone?: string;
  /** Quando passado, o card inteiro vira atalho para a tela correspondente. */
  para?: string;
}

export function StatsCard({ titulo, valor, icone: Icone, cor, tendencia, paraOIcone, para }: Props) {
  const chipIcone = <Icone className="w-5 h-5 text-white" />;

  const conteudo = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-brand uppercase tracking-wide mb-1">{titulo}</p>
        <p className="text-2xl font-bold text-foreground">{valor}</p>
        {tendencia && <p className="text-xs text-emerald-600 mt-1">{tendencia}</p>}
      </div>
      {/* `para` já cobre o card todo: um `Link` dentro de outro é HTML inválido,
          então aí o chip fica sendo só o desenho. */}
      {paraOIcone && !para ? (
        <Link
          to={paraOIcone}
          title={titulo}
          className={cn('p-2.5 rounded-lg shrink-0 hover:opacity-80 transition-opacity', CORES[cor])}
        >
          {chipIcone}
        </Link>
      ) : (
        <div className={cn('p-2.5 rounded-lg shrink-0', CORES[cor])}>{chipIcone}</div>
      )}
    </div>
  );

  const classesDoCard =
    'bg-surface-raised rounded-lg p-5 border-2 border-brand/20 hover:border-brand/40 transition-colors';

  if (para) {
    return (
      <Link to={para} title={titulo} className={cn(classesDoCard, 'block')}>
        {conteudo}
      </Link>
    );
  }

  return <div className={classesDoCard}>{conteudo}</div>;
}
